'use client';

import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  IconButton,
  Button,
  Badge,
  Tabs,
  EmptyState,
  ScrollArea,
  Icon,
  logger,
} from '@axon/ui';
import {
  LuX,
  LuCheckCheck,
  LuTrash2,
  LuBell,
  LuSettings,
  LuCircleCheck,
  LuCircleAlert,
  LuTriangleAlert,
  LuInfo,
} from 'react-icons/lu';
import { formatDistanceToNow } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { useTranslations } from 'next-intl';

import { useNotifications } from '@/contexts/notification-context';
import type { InAppNotification } from '@/lib/notification-manager';

interface NotificationCenterProps {
  onClose: () => void;
}

const typeIcons = {
  success: LuCircleCheck,
  error: LuCircleAlert,
  warning: LuTriangleAlert,
  info: LuInfo,
};

const typeColors = {
  success: 'green.500',
  error: 'red.500',
  warning: 'yellow.500',
  info: 'blue.500',
};

export function NotificationCenter({ onClose }: NotificationCenterProps) {
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll, preferences } =
    useNotifications();
  const t = useTranslations('notifications');

  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Filter notifications
  const filteredNotifications = notifications.filter((n) => {
    if (filter === 'unread' && n.read) return false;
    if (selectedCategory && n.category !== selectedCategory) return false;
    return true;
  });

  // Get unique categories
  const categories = Array.from(new Set(notifications.map((n) => n.category).filter(Boolean)));

  const handleNotificationClick = (notification: InAppNotification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
  };

  const formatTime = (date: Date) => {
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: preferences.enableToasts ? tr : enUS, // Use Turkish if enabled
    });
  };

  return (
    <Box
      position="fixed"
      top="0"
      right="0"
      height="100vh"
      width="100%"
      backgroundColor="white"
      boxShadow="-4px 0 24px rgba(0, 0, 0, 0.1)"
      zIndex={100}
      display="flex"
      direction="column"
    >
      {/* Header */}
      <HStack
        padding={4}
        borderBottomWidth="1px"
        borderColor="border.default"
        justifyContent="space-between"
      >
        <HStack gap={2}>
          <Icon asChild size="md">
            <LuBell />
          </Icon>
          <Heading size="md">{t('title')}</Heading>
          {unreadCount > 0 && <Badge colorPalette="red">{unreadCount}</Badge>}
        </HStack>
        <HStack gap={1}>
          <IconButton
            aria-label={t('markAllAsRead')}
            size="sm"
            variant="ghost"
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            icon={<LuCheckCheck />}
          />
          <IconButton
            aria-label={t('clearAll')}
            size="sm"
            variant="ghost"
            onClick={clearAll}
            disabled={notifications.length === 0}
            icon={<LuTrash2 />}
          />
          <IconButton
            aria-label={t('close')}
            size="sm"
            variant="ghost"
            onClick={onClose}
            icon={<LuX />}
          />
        </HStack>
      </HStack>

      {/* Filters */}
      <Box padding={4} borderBottomWidth="1px" borderColor="border.default">
        <Tabs.Root
          defaultValue="all"
          value={filter}
          onValueChange={(value: string) => setFilter(value as 'all' | 'unread')}
        >
          <Tabs.List>
            <Tabs.Trigger value="all">{t('allTab', { count: notifications.length })}</Tabs.Trigger>
            <Tabs.Trigger value="unread">{t('unreadTab', { count: unreadCount })}</Tabs.Trigger>
          </Tabs.List>
        </Tabs.Root>

        {categories.length > 0 && (
          <HStack gap={2} marginTop={3} flexWrap="wrap">
            <Button
              size="sm"
              variant={selectedCategory === null ? 'primary' : 'outline'}
              colorPalette="gray"
              onClick={() => setSelectedCategory(null)}
            >
              {t('allCategories')}
            </Button>
            {categories.map((category) => (
              <Button
                key={category}
                size="sm"
                variant={selectedCategory === category ? 'primary' : 'outline'}
                colorPalette="gray"
                onClick={() => setSelectedCategory(category || null)}
              >
                {category}
              </Button>
            ))}
          </HStack>
        )}
      </Box>

      {/* Notification List */}
      <Box flex="1" overflow="hidden">
        <ScrollArea height="100%">
          {filteredNotifications.length === 0 ? (
            <Box padding={8}>
              <EmptyState
                variant="no-data"
                title={t('noNotificationsTitle')}
                description={
                  filter === 'unread' ? t('noUnreadDescription') : t('noNotificationsDescription')
                }
              />
            </Box>
          ) : (
            <VStack align="stretch" gap={0}>
              {filteredNotifications.map((notification) => {
                const TypeIcon = typeIcons[notification.type];
                const color = typeColors[notification.type];

                return (
                  <Box
                    key={notification.id}
                    padding={4}
                    borderBottomWidth="1px"
                    borderColor="border.default"
                    backgroundColor={notification.read ? 'white' : 'blue.50'}
                    cursor="pointer"
                    _hover={{ backgroundColor: 'bg.subtle' }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <HStack gap={3} align="flex-start">
                      <Box color={color} flexShrink={0}>
                        <Icon asChild size="md">
                          <TypeIcon />
                        </Icon>
                      </Box>
                      <VStack align="stretch" gap={1} flex="1">
                        <Text fontWeight={notification.read ? 'normal' : 'semibold'}>
                          {notification.title}
                        </Text>
                        {notification.description && (
                          <Text fontSize="sm" color="fg.muted">
                            {notification.description}
                          </Text>
                        )}
                        <HStack gap={2} fontSize="xs" color="fg.muted">
                          <Text>{formatTime(notification.timestamp)}</Text>
                          {notification.category && (
                            <>
                              <Text>•</Text>
                              <Text>{notification.category}</Text>
                            </>
                          )}
                        </HStack>
                        {notification.actions && notification.actions.length > 0 && (
                          <HStack gap={2} marginTop={2}>
                            {notification.actions.map((action, index) => (
                              <Button
                                key={index}
                                size="sm"
                                variant={action.primary ? 'primary' : 'outline'}
                                colorPalette="blue"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  // Handle action
                                  logger.info('Action clicked:', { action: action.action });
                                }}
                              >
                                {action.label}
                              </Button>
                            ))}
                          </HStack>
                        )}
                      </VStack>
                      {!notification.read && (
                        <Box
                          width="8px"
                          height="8px"
                          borderRadius="full"
                          backgroundColor="blue.600"
                          flexShrink={0}
                        />
                      )}
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          )}
        </ScrollArea>
      </Box>

      {/* Footer */}
      <HStack padding={4} borderTopWidth="1px" borderColor="border.default" justifyContent="center">
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<LuSettings />}
          onClick={() => {
            // Open preferences modal
            logger.info('Open preferences');
          }}
        >
          {t('notificationSettings')}
        </Button>
      </HStack>
    </Box>
  );
}
